// Historische Fiat→EUR-Umrechnung für Importe, deren Anschaffungskosten/Erlöse
// NICHT in EUR notiert sind (z.B. MoonPay-Käufe in USD).
//
// Hintergrund: Die Engine führt `preis`/`gebuehr` durchgängig als EUR (FIFO,
// Reporting, Anschaffungskosten in EUR). Eine in USD bezahlte TX würde sonst
// den USD-Zahlenwert als EUR verbuchen → Kostenbasis um den Wechselkurs
// verfälscht. Wir rechnen daher mit dem ECB-Tageskurs des HANDELSTAGS um
// (frankfurter.app, ECB-Referenzkurse, kein API-Key, CORS-frei).
//
// Robustheit: fail-open. Schlägt der Kurs-Abruf fehl (offline/Rate-Limit), bleibt
// die TX unverändert und wird mit `_fxUnconverted` markiert + Warnung — besser
// ein sichtbarer Hinweis als ein still falscher Bestand.

// Kanonische Domain (frankfurter.app 301-redirectet hierher). Direkt nutzen,
// spart den Redirect-Roundtrip.
const FX_HOST = 'https://api.frankfurter.dev/v1';
const _rateCache = new Map(); // key: `${ccy}_${date}` → number | null

/**
 * Liefert den Umrechnungskurs 1 <ccy> = X EUR für ein Datum (YYYY-MM-DD).
 * EUR → 1. Bei Fehler → null (Aufrufer entscheidet fail-open).
 * frankfurter liefert für Wochenenden/Feiertage automatisch den letzten
 * ECB-Handelstag.
 */
export async function getFiatToEurRate(ccy, date) {
  const cur = String(ccy || '').toUpperCase().trim();
  if (!cur || cur === 'EUR') return 1;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) return null;
  if (typeof fetch !== 'function') return null;

  const key = `${cur}_${date}`;
  if (_rateCache.has(key)) return _rateCache.get(key);

  try {
    const res = await fetch(`${FX_HOST}/${date}?from=${cur}&to=EUR`);
    if (!res.ok) { _rateCache.set(key, null); return null; }
    const data = await res.json();
    const rate = data?.rates?.EUR;
    const val = (typeof rate === 'number' && rate > 0) ? rate : null;
    _rateCache.set(key, val);
    return val;
  } catch {
    _rateCache.set(key, null);
    return null;
  }
}

/**
 * Rechnet alle TX mit `_fxCcy` (≠ EUR) in EUR um: preis, gebuehr und stueck
 * werden mit dem Tageskurs multipliziert. `_fxCcy` wird entfernt (persistiert
 * nicht). Erfolgreiche Umrechnung hinterlässt `_fx` (Audit) + Notiz-Hinweis.
 *
 * @param {Array<object>} txs   kanonische TX (werden in-place mutiert)
 * @param {(msg:string)=>void} [addWarning]
 * @returns {Promise<{converted:number, failed:number}>}
 */
export async function convertFiatPricesToEur(txs, addWarning) {
  let converted = 0, failed = 0;
  for (const tx of txs || []) {
    const ccy = tx._fxCcy;
    if (!ccy || String(ccy).toUpperCase() === 'EUR') {
      if (tx._fxCcy) delete tx._fxCcy;
      continue;
    }
    const rate = await getFiatToEurRate(ccy, tx.datum);
    if (rate == null) {
      tx._fxUnconverted = true;
      delete tx._fxCcy;
      failed++;
      addWarning?.(`Wechselkurs ${String(ccy).toUpperCase()}→EUR für ${tx.datum} nicht verfügbar — Betrag unverändert (bitte im Review prüfen).`);
      continue;
    }
    const preisOrig = tx.preis;
    if (typeof tx.preis === 'number')   tx.preis   = tx.preis   * rate;
    if (typeof tx.gebuehr === 'number') tx.gebuehr = tx.gebuehr * rate;
    if (typeof tx.stueck === 'number')  tx.stueck  = tx.stueck  * rate;
    tx._fx = { ccy: String(ccy).toUpperCase(), rate, date: tx.datum, preisOrig };
    tx.notiz = [tx.notiz, `umgerechnet ${String(ccy).toUpperCase()}→EUR @ ${rate.toFixed(4)}`]
      .filter(Boolean).join(' · ');
    delete tx._fxCcy;
    converted++;
  }
  return { converted, failed };
}
